export type CanonicalValue =
  | null
  | boolean
  | number
  | string
  | readonly CanonicalValue[]
  | { readonly [key: string]: CanonicalValue };

const DOMAIN_PREFIX = utf8Encode("BindingTrustWitness\u0000v1\u0000");
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]*$/u;
const BASE64_CHUNK_BYTES = 24_576;

export class CryptoEncodingError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "CryptoEncodingError";
  }
}

export function utf8Encode(value: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(value);
}

export function utf8Decode(value: BufferSource): string {
  return new TextDecoder("utf-8", { fatal: true }).decode(toUint8Array(value));
}

export function toUint8Array(value: BufferSource): Uint8Array<ArrayBuffer> {
  if (value instanceof Uint8Array) {
    return new Uint8Array(value);
  }

  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value.slice(0));
  }

  return new Uint8Array(
    value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength),
  );
}

export function concatBytes(
  ...values: readonly Uint8Array<ArrayBuffer>[]
): Uint8Array<ArrayBuffer> {
  const length = values.reduce((total, value) => total + value.byteLength, 0);
  const result = new Uint8Array(length);
  let offset = 0;
  for (const value of values) {
    result.set(value, offset);
    offset += value.byteLength;
  }
  return result;
}

export function base64UrlEncode(value: BufferSource): string {
  const bytes = toUint8Array(value);
  let encoded = "";

  for (let offset = 0; offset < bytes.byteLength; offset += BASE64_CHUNK_BYTES) {
    const chunk = bytes.subarray(offset, offset + BASE64_CHUNK_BYTES);
    let binary = "";
    for (let index = 0; index < chunk.byteLength; index += 1) {
      binary += String.fromCharCode(chunk[index] ?? 0);
    }
    encoded += btoa(binary);
  }

  return encoded.replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export function base64UrlDecode(
  value: string,
  maximumBytes?: number,
): Uint8Array<ArrayBuffer> {
  if (!BASE64URL_PATTERN.test(value) || value.length % 4 === 1) {
    throw new CryptoEncodingError("Invalid base64url encoding");
  }

  const estimatedBytes = Math.floor((value.length * 3) / 4);
  if (maximumBytes !== undefined && estimatedBytes > maximumBytes) {
    throw new CryptoEncodingError("Decoded value exceeds its size limit");
  }

  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  let binary: string;
  try {
    binary = atob(value.replaceAll("-", "+").replaceAll("_", "/") + padding);
  } catch {
    throw new CryptoEncodingError("Invalid base64url encoding");
  }

  if (maximumBytes !== undefined && binary.length > maximumBytes) {
    throw new CryptoEncodingError("Decoded value exceeds its size limit");
  }

  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  if (base64UrlEncode(bytes) !== value) {
    throw new CryptoEncodingError("Non-canonical base64url encoding");
  }

  return bytes;
}

export function canonicalJson(value: unknown): string {
  return serializeCanonical(value, new Set<object>());
}

export function serializeForDomain(
  domain: string,
  value: unknown,
): Uint8Array<ArrayBuffer> {
  if (domain.length === 0) {
    throw new CryptoEncodingError("A serialization domain is required");
  }

  const domainBytes = utf8Encode(domain);
  const payloadBytes = utf8Encode(canonicalJson(value));
  if (domainBytes.byteLength > 0xffff || payloadBytes.byteLength > 0xffff_ffff) {
    throw new CryptoEncodingError("Serialized input is too large");
  }

  const lengths = new Uint8Array(6);
  const view = new DataView(lengths.buffer);
  view.setUint16(0, domainBytes.byteLength, false);
  view.setUint32(2, payloadBytes.byteLength, false);
  return concatBytes(DOMAIN_PREFIX, lengths, domainBytes, payloadBytes);
}

export async function sha256(value: BufferSource): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", value));
}

export async function hashForDomain(
  domain: string,
  value: unknown,
): Promise<string> {
  return base64UrlEncode(await sha256(serializeForDomain(domain, value)));
}

export function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  if (!Number.isSafeInteger(length) || length <= 0 || length > 65_536) {
    throw new CryptoEncodingError("Invalid random byte length");
  }

  return crypto.getRandomValues(new Uint8Array(length));
}

export function constantTimeEqual(left: BufferSource, right: BufferSource): boolean {
  const leftBytes = toUint8Array(left);
  const rightBytes = toUint8Array(right);
  let difference = leftBytes.byteLength ^ rightBytes.byteLength;
  const length = Math.max(leftBytes.byteLength, rightBytes.byteLength);

  for (let index = 0; index < length; index += 1) {
    difference |= (leftBytes[index] ?? 0) ^ (rightBytes[index] ?? 0);
  }

  return difference === 0;
}

function serializeCanonical(value: unknown, ancestors: Set<object>): string {
  if (value === null || typeof value === "boolean" || typeof value === "string") {
    return JSON.stringify(value);
  }

  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || Object.is(value, -0)) {
      throw new CryptoEncodingError("Canonical numbers must be safe integers");
    }
    return JSON.stringify(value);
  }

  if (typeof value !== "object") {
    throw new CryptoEncodingError("Unsupported canonical value");
  }

  if (ancestors.has(value)) {
    throw new CryptoEncodingError("Canonical values must not be cyclic");
  }
  ancestors.add(value);

  try {
    if (Array.isArray(value)) {
      return `[${value.map((item) => serializeCanonical(item, ancestors)).join(",")}]`;
    }

    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      throw new CryptoEncodingError("Canonical objects must be plain objects");
    }

    const record = value as Readonly<Record<string, unknown>>;
    const properties = Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${serializeCanonical(record[key], ancestors)}`);
    return `{${properties.join(",")}}`;
  } finally {
    ancestors.delete(value);
  }
}
